import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useMemo, useRef } from 'react';
import styles from './WorkFilterVisuals.module.scss';
import { expand, setupTransitionInTimeline } from './WorkFilterVisuals.transitions';
import type { WorkFilterVisualsProps, WorkFilterVisualsRef } from './WorkFilterVisuals.data';
import { findTransitionController, useScrollTransition } from 'util/react-transition-component';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import useMount from '../../../hooks/useMount';
import WorkFilterVisualItem from './work-filter-visual-item/WorkFilterVisualItem';
import { dataSourceToUrlQuery } from 'util/url.utils';
import type { WorkFilterVisualItemRef } from './work-filter-visual-item/WorkFilterVisualItem.data';
import useWindowSize from '../../../hooks/useWindowSize';
import { LottieId } from '@/data/lottie.config';

export default function WorkFilterVisuals({
  activeCategory,
  categories,
  ref,
  ...props
}: WorkFilterVisualsProps & { ref?: Ref<WorkFilterVisualsRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const containerRef = useRef<HTMLDivElement>(null);
  const previousIndex = useRef<number>(undefined);
  const itemsRef = useRef<Array<WorkFilterVisualItemRef>>([]);

  const { width, height } = useWindowSize();

  useScrollTransition({
    scroll: {
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const activeIndex = useMemo(() => {
    if (activeCategory) {
      return categories?.indexOf(activeCategory);
    }
    return -1;
  }, [activeCategory, categories]);

  useUpdateEffect(() => {
    const isOpen = typeof activeIndex === 'number' && activeIndex >= 0;
    expand(containerRef, isOpen, isOpen ? itemsRef?.current[activeIndex] : undefined, false);

    (async () => {
      if (typeof previousIndex?.current === 'number' && previousIndex?.current >= 0) {
        const controller = findTransitionController(
          itemsRef?.current[previousIndex.current].elementRef,
        );
        await controller?.transitionOut();
      }

      if (isOpen) {
        const controller = findTransitionController(itemsRef.current[activeIndex].elementRef);
        controller?.transitionIn();
      }
    })();

    previousIndex.current = activeIndex;
  }, [activeIndex]);

  // Resize listener
  useUpdateEffect(() => {
    const isOpen = typeof activeIndex === 'number' && activeIndex >= 0;
    if (isOpen) {
      const ref = itemsRef.current[activeIndex];
      expand(containerRef, true, isOpen ? ref : undefined, true);
    }
  }, [width, height]);

  useMount(() => {
    const isOpen = typeof activeIndex === 'number' && activeIndex >= 0;

    if (isOpen) {
      const ref = itemsRef.current[activeIndex];
      expand(containerRef, true, isOpen ? ref : undefined, true);
      const controller = findTransitionController(ref.elementRef);

      if (controller) {
        controller.transitionIn();
      }
    }

    previousIndex.current = activeIndex;
  });

  return (
    <div ref={elementRef} className={classNames(styles.workFilterVisuals, props.className)}>
      <div ref={containerRef} className={classNames(styles.container)}>
        {categories?.map((category, index) => (
          <WorkFilterVisualItem
            ref={(ref) => {
              itemsRef.current[index] = ref as WorkFilterVisualItemRef;
            }}
            key={category}
            title={category}
            icon={dataSourceToUrlQuery(category) as LottieId}
            className={classNames(styles.item)}
          />
        ))}
      </div>
    </div>
  );
}
